<template>
  <div>
    <v-row class="ma-1">
      <h4 class="font-weight-bold pa-3 mb-0">
        Individual Deductions and Rebates (TP1)
      </h4>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-autocomplete
          v-model="selectedYear"
          :items="years"
          item-text="name"
          item-value="id"
          hide-no-data
          hide-selected
          label="Year"
          placeholder="Choose year"
          @change="loadData"
        />
      </v-col>
    </v-row>

    <div
      v-if="individualDeductionAndRebates.length > 0"
      class="mx-6 px-3 pb-3"
      style="background-color: #e6e6e6"
    >
      <v-row class="font-weight-bold">
        <v-col
          cols="7"
          class="text-center"
        >
          Description
        </v-col>
        <v-col
          cols="2"
          class="text-no-wrap"
          style="border-right:1px solid white;border-left:1px solid white"
        >
          Max (RM)
        </v-col>
        <v-col
          cols="3"
          class="text-no-wrap"
        >
          Accumulated (RM)
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="panel"
        multiple
      >
        <v-expansion-panel
          v-for="(individualDeductionAndRebate,index) in individualDeductionAndRebates"
          :key="index"
        >
          <v-expansion-panel-header class="font-calibri font-16 font-weight-bold border-bottom-light-grey accordion-max-height">
            <v-row>
              <v-col
                cols="7"
                class="ma-auto"
              >
                <v-tooltip
                  bottom
                  min-width="500"
                  left
                  content-class="custom-tooltip"
                >
                  <template v-slot:activator="{ on: tooltip }">
                    <v-icon
                      small
                      color="warning"
                      v-on="{...tooltip }"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div
                    class="text-left"
                    v-html="individualDeductionAndRebate.description"
                  />
                </v-tooltip>
                &nbsp;{{ individualDeductionAndRebate.name }}
              </v-col>
          
              <v-col
                cols="2"
                class="text-right text-no-wrap ma-auto"
              >
                {{ twoDigit(individualDeductionAndRebate.maximum) }}
              </v-col>
              <v-col
                cols="2"
                class="text-right text-no-wrap ma-auto"
                :class="{
                  'red--text': individualDeductionAndRebate.maximum<accumulated(individualDeductionAndRebate), 
                }"
              >
                {{ twoDigit(accumulated(individualDeductionAndRebate)) }}
              </v-col>

              <v-col
                cols="1"
                class="px-0 text-right"
              >
                <employee-profile-payroll-payroll-company-additional-deduction-dialog-create
                  v-if="$hasPermissionTo(['create employee payroll']) && !companyEmployee.end_date && individualDeductionAndRebate.subs.length < 1"
                  :tax-relief="individualDeductionAndRebate"
                  :company-employee-uid="companyEmployee.company_employee_uid"
                  @created="loadData"
                />
              </v-col>
            </v-row>

            <template v-slot:actions>
              <v-icon
                color="primary"
                style="display:none"
              >
                $expand
              </v-icon>
            </template>
          </v-expansion-panel-header>
            
          <v-expansion-panel-content>
            <v-row v-if="individualDeductionAndRebate.subs.length>0">
              <v-col cols="12">
                <v-expansion-panels multiple>
                  <v-expansion-panel
                    v-for="(sub,index) in individualDeductionAndRebate.subs"
                    :key="index"
                  >
                    <v-expansion-panel-header class="ml-4 py-0">
                      <v-row>
                        <v-col
                          cols="7"
                          class="ma-auto"
                        >
                          <v-tooltip
                            bottom
                            min-width="500"
                            left
                            content-class="custom-tooltip"
                          >
                            <template v-slot:activator="{ on: tooltip }">
                              <v-icon
                                small
                                color="warning"
                                v-on="{...tooltip }"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <div
                              class="text-left"
                              v-html="sub.description"
                            />
                          </v-tooltip>
                          &nbsp;<span v-html="sub.name" />
                        </v-col>
                        <v-col
                          cols="2"
                          class="text-right text-no-wrap ma-auto"
                        >
                          <div>{{ twoDigit(sub.maximum) }}</div>
                        </v-col>

                        <v-col cols="2" />
                        
                        <v-col
                          cols="1"
                          class="ma-auto text-right"
                        >
                          <employee-profile-payroll-payroll-company-additional-deduction-dialog-create
                            v-if="$hasPermissionTo(['create employee payroll']) && !companyEmployee.end_date"
                            :tax-relief="sub"
                            :company-employee-uid="companyEmployee.company_employee_uid"
                            @created="loadData"
                          />
                        </v-col>
                      </v-row>

                      <template v-slot:actions>
                        <v-icon
                          color="primary"
                          style="display:none"
                        >
                          $expand
                        </v-icon>
                      </template>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row no-gutters>
                        <v-col
                          class="font-weight-bold text-center"
                          cols="2"
                        >
                          Amount (RM)
                        </v-col>
                        <v-col class="font-weight-bold text-center">
                          Payroll Period
                        </v-col>
                        <v-col />
                      </v-row>

                      <v-row
                        v-for="(record,index) in sub.records"
                        :key="index"
                        no-gutters
                      >
                        <v-col
                          class="text-right"
                          cols="2"
                        >
                          {{ twoDigit(record.amount) }}
                        </v-col>
                        <v-col class="text-center">
                          {{ $moment(record.date).format('DD MMM YYYY') }}
                        </v-col>
                        <v-col>
                          <employee-profile-payroll-payroll-company-additional-deduction-dialog-edit
                            v-if="$hasPermissionTo(['update employee payroll']) && !companyEmployee.end_date && $moment(record.date).isSame(latestPayrollPeriodDate) ||$moment(record.date).isAfter(latestPayrollPeriodDate) "
                            :record="record"
                            @updated="loadData"
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
           
            <v-row v-else>
              <v-col cols="12">
                <v-row no-gutters>
                  <v-col
                    class="font-weight-bold text-center"
                    cols="2"
                  >
                    Amount (RM)
                  </v-col>
                  <v-col class="font-weight-bold text-center">
                    Payroll Period
                  </v-col>
                  <v-col />
                </v-row>
                
                <v-row
                  v-for="(record,index) in individualDeductionAndRebate.records"
                  :key="index"
                  no-gutters
                >
                  <v-col
                    class="text-right"
                    cols="2"
                  >
                    {{ twoDigit(record.amount) }}
                  </v-col>
                  <v-col class="text-center">
                    {{ $moment(record.date).format('DD MMMM YYYY') }}
                  </v-col>
                  <v-col>
                    <employee-profile-payroll-payroll-company-additional-deduction-dialog-edit
                      v-if="$hasPermissionTo(['update employee payroll']) && !companyEmployee.end_date && $moment(record.date).isSame(latestPayrollPeriodDate) ||$moment(record.date).isAfter(latestPayrollPeriodDate) "
                      :record="record"
                      @updated="loadData"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <div v-else>
      <v-skeleton-loader
        type="text@15"
        class="mx-3"
      />
    </div>
  </div>
</template>

<script>
  export default {
    components: {
      EmployeeProfilePayrollPayrollCompanyAdditionalDeductionDialogEdit: () => import('@/components/hris/employee/profile/payroll/payroll-company/additional-deduction/DialogEdit'),
      EmployeeProfilePayrollPayrollCompanyAdditionalDeductionDialogCreate: () => import('@/components/hris/employee/profile/payroll/payroll-company/additional-deduction/DialogCreate'),
      // EmployeeProfilePayrollPayrollCompanyAdditionalDeductionDialogDelete: () => import('@/components/hris/employee/profile/payroll/payroll-company/additional-deduction/DialogDelete'),
    },
    props: {
      companyEmployee: {
        type: Object,
        default: null,
      },
    },

    data () {
      return {
        individualDeductionAndRebates: [ ],
        panel: [],
        years: [],
        latestPayrollPeriodDate: null,
        selectedYear: parseInt(this.$moment().format('YYYY')),
      }
    },

    watch: {
      companyEmployee: {
        immediate: true,
        handler (value) {
          if (value) {
            this.loadData()
          }
        },
      },
    },

    methods: {
      twoDigit (amount) {
        if (amount) {
          return (Math.round((amount / 100) * 100) / 100).toFixed(2)
        }
      },

      loadData () {
        this.individualDeductionAndRebates = []

        Promise.all([
          this.$api.taxReliefs().listYears(),
          this.$api.taxReliefs().list({ is_compulsory_deduction: 0, year: this.selectedYear }),
          this.$api.companyEmployeeAdditionalDeductions().list(this.companyEmployee.company_employee_uid, '?year=' + this.selectedYear),
          this.$api.payrollPeriods().list(),
        ]).then(([years, taxRelief, companyEmployeeAdditionalDeductions, payrollPeriods]) => {
          this.years = years.data.filter(year => {
            return year <= this.$moment().format('YYYY')
          })

          let temp = taxRelief.data
          temp.map(item => {
            if (!item.parent_id) {
              this.individualDeductionAndRebates.push({
                id: item.id,
                name: item.name,
                description: item.description,
                accumulated: 0,
                maximum: item.amount,
                records: [],
                subs: [],
              })
            } else {
              let specificIndividualDeductionAndRebate = this.individualDeductionAndRebates.find(deductionOrRebate => deductionOrRebate.id === item.parent_id)

              specificIndividualDeductionAndRebate.subs.push({
                id: item.id,
                name: item.name,
                description: item.description,
                maximum: item.amount,
                records: [],
              })
            }
          })

          let temp2 = companyEmployeeAdditionalDeductions.data

          if (temp2.length > 0) {
            temp2.map(item => {
              if (!item.tax_relief_parent_id) {
                let specificIndividualDeductionAndRebate = this.individualDeductionAndRebates.find(deductionOrRebate => deductionOrRebate.id === item.tax_relief_id)

                specificIndividualDeductionAndRebate.records.push({
                  id: item.id,
                  amount: item.amount,
                  date: item.process_date,
                })
              } else {
                let specificIndividualDeductionAndRebate = this.individualDeductionAndRebates.find(deductionOrRebate => deductionOrRebate.id === item.tax_relief_parent_id)

                let sub = specificIndividualDeductionAndRebate.subs.find(sub => sub.id === item.tax_relief_id)
        
                sub.records.push({
                  id: item.id,
                  amount: item.amount,
                  date: item.process_date,
                })
              }
            }) 
          }

          this.individualDeductionAndRebates.map((item, index) => {
            if (item.subs.length > 0) {
              this.panel.push(index)
            }
          })
      
          this.latestPayrollPeriodDate = payrollPeriods.data[0].payroll_date
        })
      },
      accumulated (individualDeductionAndRebate) {
        let accumulated = 0
   
        if (individualDeductionAndRebate.records.length > 0) {
          individualDeductionAndRebate.records.forEach(element => {
            accumulated = accumulated + element.amount
          })
        }       
        
        if (individualDeductionAndRebate.subs.length > 0) {
          individualDeductionAndRebate.subs.forEach(element => {
            element.records.forEach(element => {
              accumulated = accumulated + element.amount
            })
          })
        }

        return accumulated
      },
    },
  }
</script>

<style scoped>
.font-calibri{
  font-family: Calibri !important;
}

.font-16{
  font-size:16px;
}

.font-14{
  font-size:14px;
}

.border-bottom-light-grey{
  border-bottom:1px solid #e6e6e6;
}

.accordion-max-height{
  max-height: 36px !important;
}

.custom-tooltip {
  opacity: 1!important;
}
</style>
